<template>
    <div class="layout">
        <div v-if="selectedLayoutId === 0" class="search-save-button">
            <div class="search">
                <ds-input-field
                    v-model="searchCriteria"
                    class="search-input-field"
                    label="Search Contact info"
                >
                    <template #trailing>
                        <span
                            v-show="searchCriteria"
                            class="clear-icon"
                            @click="clearSearch"
                        >
                            <ds-icon name="x" />
                        </span>
                    </template>
                </ds-input-field>
            </div>

            <contact-info-header
                :contact-record-details="contactRecordDetails"
                :is-contact-info-changed="isContactInfoChanged"
                :user-id="userId.toString()"
                :app-id="appId.toString()"
            />
        </div>
        <div v-if="selectedLayoutId === 0">
            <div v-if="checkHeadersDisplay('generalInformation')" class="profile-label">
                <div class="section-header">
                    <h3>General Information</h3>
                </div>

                <div class="input-grid">
                    <ds-input-field
                        v-if="displayField('firstName', 'generalInformation')"
                        v-model="contactRecordDetails.firstName"
                        data-qa="first-name"
                        label="First name"
                    />

                    <ds-input-field
                        v-if="displayField('lastName', 'generalInformation')"
                        v-model="contactRecordDetails.lastName"
                        data-qa="last-name"
                        label="Last name"
                        name="lastname"
                    />

                    <div style="display: flex;">
                        <ds-multiselect
                            v-if="isLinkToCompanyRecordEnabled && displayField('companies', 'generalInformation')"
                            ref="companyMultiselect"
                            v-model="contactRecordDetails.companyID"
                            :options="companies"
                            add-text="Add New Company"
                            allow-add
                            bind-value-only
                            data-qa="companies"
                            name="companyDrop"
                            placeholder="Company"
                            searchable
                            show-search-icon
                            @add="addCompanyModal"
                        />
                        <span v-if="isLinkToCompanyRecordEnabled && displayField('companies', 'generalInformation') && contactRecordDetails.companyID > 0" style="padding: 1px;">
                            <ds-tooltip position="top">
                                <template #default>
                                    <div>View Company</div>
                                </template>
                                <template #reference>
                                    <ds-icon-button name="external-link" @click="viewCompany" />
                                </template>
                            </ds-tooltip>
                        </span>
                    </div>

                    <ds-multiselect
                        v-if="!isLinkToCompanyRecordEnabled && displayField('companies', 'generalInformation')"
                        ref="companyMultiselect"
                        v-model="contactRecordDetails.companyID"
                        :options="companies"
                        add-text="Add New Company"
                        allow-add
                        bind-value-only
                        data-qa="companies"
                        name="companyDrop"
                        placeholder="Company"
                        searchable
                        show-search-icon
                        @add="addCompanyModal"
                    />

                    <ds-input-field
                        v-if="displayField('jobTitle', 'generalInformation')"
                        v-model="contactRecordDetails.jobTitle"
                        data-qa="job-title"
                        label="Job title"
                        name="jobtitle"
                    />

                    <ds-multiselect
                        v-if="displayField('personType', 'generalInformation')"
                        v-model="contactRecordDetails.personType"
                        :options="personType"
                        bind-value-only
                        data-qa="person-type"
                        name="personType"
                        placeholder="Person Type"
                        searchable
                    />

                    <ds-multiselect
                        v-if="displayField('leadSource', 'generalInformation')"
                        v-model="contactRecordDetails.leadSource"
                        :options="leadSourcesDrop"
                        bind-value-only
                        data-qa="lead-source"
                        name="leadsource"
                        placeholder="Lead source"
                        searchable
                        :readonly="!contactPermissionDTO.canChangeLeadSource"
                    />

                    <ds-select-field
                        v-if="contactPermissionDTO.canSeeOtherUsers && (displayField('owners', 'generalInformation'))"
                        v-model="contactRecordDetails.owner"
                        :options="ownersDrop"
                        bind-value-only
                        data-qa="owner"
                        label="Owner"
                        name="owners"
                        :readonly="!contactPermissionDTO.canReassign"
                    />
                </div>
            </div>

            <div v-if="checkHeadersDisplay('accountSummaryAndLeadScore')" class="profile-label slides">
                <div class="section-header">
                    <h3><span v-if="contactPermissionDTO.canSeeSummary">Account Summary &amp; </span>Lead Score</h3>
                </div>

                <div v-if="contactPermissionDTO.canSeeSummary && (displayField('amountPaid', 'accountSummaryAndLeadScore'))" class="account-summary">
                    <div class="summary">
                        <div class="account" data-qa="amount-paid">
                            Amt paid
                        </div>

                        <p class="paid">
                            {{ contactRecordDetails.amountPaid }}
                        </p>
                    </div>

                    <div v-if="contactPermissionDTO.canSeeSummary && (displayField('balanceDue', 'accountSummaryAndLeadScore'))" class="summary">
                        <div class="account" data-qa="balance-due">
                            Bal due
                        </div>

                        <p class="due">
                            {{ contactRecordDetails.billDue }}
                        </p>
                    </div>

                    <div v-if="contactPermissionDTO.canSeeSummary && (displayField('invoice', 'accountSummaryAndLeadScore'))" class="summary">
                        <div class="account">
                            <ds-text-button
                                id="backToTop"
                                dense
                                data-qa="view-invoices"
                                leading-icon="external-link"
                                @click="viewInvoices"
                            >
                                Invoices
                            </ds-text-button>
                        </div>
                    </div>

                    <div v-if="displayField('leadScore', 'accountSummaryAndLeadScore')" class="lead-score">
                        <div class="account">
                            Lead Score
                        </div>

                        <span v-for="({ leadScore, i }) in leadScoreValue" :key="i">
                            <ds-icon class="fire-icon" name="fire-fill" />
                        </span>

                        <span v-for="({ leadScore, i }) in (5 - leadScoreValue)" :key="i">
                            <ds-icon class="fire-icon" name="fire" />
                        </span>

                        <span class="score">{{ leadScoreValue }}/5</span>
                    </div>
                </div>
            </div>

            <div v-if="checkField('personNotes')" class="profile-label">
                <div class="section-header">
                    <h3>Person Notes</h3>
                </div>

                <ds-text-area-field
                    id="notes"
                    v-model="contactRecordDetails.notes"
                    class="person-notes"
                    data-qa="notes"
                    name="notes"
                    label="Person Notes"
                    :maxlength="maxCharacter"
                />
            </div>

            <div v-if="checkHeadersDisplay('emailSocialMedia')" class="profile-label">
                <div class="section-header">
                    <h3>Email &amp; Social Media</h3>
                </div>

                <div class="input-grid">
                    <div v-if="displayField('email', 'emailSocialMedia')">
                        <ds-input-field
                            v-model="contactRecordDetails.email"
                            data-qa="email"
                            label="Email"
                            name="email"
                        >
                            <ds-icon
                                v-show="!checkEmailLockdown && socialFieldEmptyCheck(contactRecordDetails.email)"
                                slot="trailing"
                                class="external-look"
                                name="send"
                                @click.native.stop="launchBardEmailBroadcast(contactRecordDetails.email)"
                            />
                        </ds-input-field>

                        <div v-if="socialFieldEmptyCheck(contactRecordDetails.email)" class="email2-status">
                            Email status:
                            <div class="status">
                                <a :href="emailStatus(contactRecordDetails.email)" target="_blank"> {{ isEmailStatusFullNameEnabled ? contactRecordDetails.emailOptOutStatus : contactRecordDetails.emailStatus }}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div v-if="displayField('email2', 'emailSocialMedia')">
                        <ds-input-field
                            v-model="contactRecordDetails.email2"
                            data-qa="email2"
                            label="Email 2"
                            name="email2"
                        >
                            <ds-icon
                                v-show="!checkEmailLockdown && socialFieldEmptyCheck(contactRecordDetails.email2)"
                                slot="trailing"
                                class="external-look"
                                name="send"
                                @click.native.stop="launchBardEmailBroadcast(contactRecordDetails.email2)"
                            />
                        </ds-input-field>

                        <div v-if="socialFieldEmptyCheck(contactRecordDetails.email2)" class="email2-status">
                            Email status:
                            <div class="status">
                                <a :href="emailStatus(contactRecordDetails.email2)" target="_blank"> {{ isEmailStatusFullNameEnabled ? contactRecordDetails.email2OptOutStatus : contactRecordDetails.email2Status }}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div v-if="displayField('email3', 'emailSocialMedia')">
                        <ds-input-field
                            v-model="contactRecordDetails.email3"
                            data-qa="email3"
                            label="Email 3"
                        >
                            <ds-icon
                                v-show="!checkEmailLockdown && socialFieldEmptyCheck(contactRecordDetails.email3)"
                                slot="trailing"
                                class="external-look"
                                name="send"
                                @click.native.stop="launchBardEmailBroadcast(contactRecordDetails.email3)"
                            />
                        </ds-input-field>

                        <div v-if="socialFieldEmptyCheck(contactRecordDetails.email3)" class="email2-status">
                            Email status:
                            <div class="status">
                                <a :href="emailStatus(contactRecordDetails.email3)" target="_blank"> {{ isEmailStatusFullNameEnabled ? contactRecordDetails.email3OptOutStatus : contactRecordDetails.email3Status }}
                                </a>
                            </div>
                        </div>
                    </div>

                    <ds-input-field
                        v-if="displayField('website', 'emailSocialMedia')"
                        v-model="contactRecordDetails.website"
                        label="Website"
                        data-qa="website"
                    >
                        <ds-icon
                            v-show="socialFieldEmptyCheck(contactRecordDetails.website)"
                            slot="trailing"
                            class="external-look"
                            name="external-link"
                            @click.native.stop="webSite"
                        />
                    </ds-input-field>

                    <ds-input-field
                        v-if="displayField('facebook', 'emailSocialMedia')"
                        v-model="contactRecordDetails.facebook"
                        label="Facebook"
                        data-qa="facebook"
                    >
                        <ds-icon
                            v-show="socialFieldEmptyCheck(contactRecordDetails.facebook)"
                            slot="trailing"
                            class="external-look"
                            name="external-link"
                            @click.native.stop="facebook"
                        />
                    </ds-input-field>

                    <ds-input-field
                        v-if="displayField('twitter', 'emailSocialMedia')"
                        v-model="contactRecordDetails.twitter"
                        label="Twitter"
                        data-qa="twitter"
                    >
                        <ds-icon
                            v-show="socialFieldEmptyCheck(contactRecordDetails.twitter)"
                            slot="trailing"
                            class="external-look"
                            name="external-link"
                            @click.native.stop="twitter"
                        />
                    </ds-input-field>

                    <ds-input-field
                        v-if="displayField('linkedIn', 'emailSocialMedia')"
                        v-model="contactRecordDetails.linkedin"
                        label="LinkedIn"
                        data-qa="linkedIn"
                    >
                        <ds-icon
                            v-show="socialFieldEmptyCheck(contactRecordDetails.linkedin)"
                            slot="trailing"
                            class="external-look"
                            name="external-link"
                            @click.native.stop="linkedIn"
                        />
                    </ds-input-field>

                    <ds-input-field
                        v-if="displayField('instagram', 'emailSocialMedia')"
                        v-model="contactRecordDetails.instragram"
                        label="Instagram"
                        data-qa="instagram"
                    >
                        <ds-icon
                            v-show="socialFieldEmptyCheck(contactRecordDetails.instragram)"
                            slot="trailing"
                            class="external-look"
                            name="external-link"
                            @click.native.stop="instagram"
                        />
                    </ds-input-field>

                    <ds-input-field
                        v-if="displayField('snapchat', 'emailSocialMedia')"
                        v-model="contactRecordDetails.snapchat"
                        label="Snapchat"
                        data-qa="snapchat"
                    >
                        <ds-icon
                            v-show="socialFieldEmptyCheck(contactRecordDetails.snapchat)"
                            slot="trailing"
                            class="external-look"
                            name="external-link"
                            @click.native.stop="snapchat"
                        />
                    </ds-input-field>

                    <ds-input-field
                        v-if="displayField('youtube', 'emailSocialMedia')"
                        v-model="contactRecordDetails.youtube"
                        label="Youtube"
                        data-qa="youtube"
                    >
                        <ds-icon
                            v-show="socialFieldEmptyCheck(contactRecordDetails.youtube)"
                            slot="trailing"
                            class="external-look"
                            name="external-link"
                            @click.native.stop="youtube"
                        />
                    </ds-input-field>

                    <ds-input-field
                        v-if="displayField('pinterest', 'emailSocialMedia')"
                        v-model="contactRecordDetails.pinterest"
                        label="Pinterest"
                        data-qa="pinterest"
                    >
                        <ds-icon
                            v-show="socialFieldEmptyCheck(contactRecordDetails.pinterest)"
                            slot="trailing"
                            class="external-look"
                            name="external-link"
                            @click.native.stop="pinterest"
                        />
                    </ds-input-field>
                </div>
            </div>

            <div v-if="checkHeadersDisplay('phoneAndFax')" class="profile-label">
                <div class="section-header">
                    <h3>Phone &amp; Fax</h3>
                </div>

                <div class="input-grid">
                    <div class="input-group">
                        <ds-input-field
                            v-if="displayField('phone1', 'phoneAndFax')"
                            v-model="contactRecordDetails.phone1.number"
                            data-qa="phone1"
                            label="Phone 1"
                            name="phone1"
                        />

                        <ds-select-field
                            v-if="displayField('phone1type', 'phoneAndFax')"
                            v-model="contactRecordDetails.phone1.type"
                            :options="phoneTypeDrop"
                            bind-value-only
                            data-qa="phone1-type"
                            label="Type"
                            name="phone1type"
                        />
                    </div>

                    <div class="input-group">
                        <ds-input-field
                            v-if="displayField('phone2', 'phoneAndFax')"
                            v-model="contactRecordDetails.phone2.number"
                            data-qa="phone2"
                            label="Phone 2"
                            name="phone2"
                        />

                        <ds-select-field
                            v-if="displayField('phone2type', 'phoneAndFax')"
                            v-model="contactRecordDetails.phone2.type"
                            :options="phoneTypeDrop"
                            bind-value-only
                            data-qa="phone2-type"
                            label="Type"
                            name="phone2type"
                        />
                    </div>

                    <div class="input-group">
                        <ds-input-field
                            v-if="displayField('phone3', 'phoneAndFax')"
                            v-model="contactRecordDetails.phone3.number"
                            data-qa="phone3"
                            label="Phone 3"
                            name="phone3"
                        />

                        <ds-select-field
                            v-if="displayField('phone3type', 'phoneAndFax')"
                            v-model="contactRecordDetails.phone3.type"
                            :options="phoneTypeDrop"
                            bind-value-only
                            data-qa="phone3-type"
                            label="Type"
                            name="phone3type"
                        />
                    </div>

                    <div class="input-group">
                        <ds-input-field
                            v-if="displayField('phone4', 'phoneAndFax')"
                            v-model="contactRecordDetails.phone4.number"
                            data-qa="phone4"
                            label="Phone 4"
                            name="phone4"
                        />

                        <ds-select-field
                            v-if="displayField('phone4type', 'phoneAndFax')"
                            v-model="contactRecordDetails.phone4.type"
                            :options="phoneTypeDrop"
                            bind-value-only
                            data-qa="phone4-type"
                            label="Type"
                            name="phone4type"
                        />
                    </div>

                    <div class="input-group">
                        <ds-input-field
                            v-if="displayField('phone5', 'phoneAndFax')"
                            v-model="contactRecordDetails.phone5.number"
                            data-qa="phone5"
                            label="Phone 5"
                            name="phone5"
                        />

                        <ds-select-field
                            v-if="displayField('phone5type', 'phoneAndFax')"
                            v-model="contactRecordDetails.phone5.type"
                            :options="phoneTypeDrop"
                            bind-value-only
                            data-qa="phone5-type"
                            label="Type"
                            name="phone5type"
                        />
                    </div>

                    <ds-input-field
                        v-if="displayField('fax1', 'phoneAndFax')"
                        v-model="contactRecordDetails.fax1"
                        data-qa="fax1"
                        label="Fax 1"
                        name="fax1"
                    />

                    <ds-input-field
                        v-if="displayField('fax2', 'phoneAndFax')"
                        v-model="contactRecordDetails.fax2"
                        data-qa="fax2"
                        label="Fax 2"
                        name="fax2"
                    />
                </div>
            </div>

            <div v-if="checkHeadersDisplay('billingAddress')" class="profile-label">
                <div class="section-header">
                    <h3>Billing Address</h3>
                </div>

                <div class="input-grid">
                    <ds-input-field
                        v-if="displayField('billingStreetAddress1', 'billingAddress')"
                        v-model="contactRecordDetails.billingAddress.street1"
                        data-qa="billing-street1"
                        label="Street address 1"
                        name="billing-street1"
                    />

                    <ds-input-field
                        v-if="displayField('billingStreetAddress2', 'billingAddress')"
                        v-model="contactRecordDetails.billingAddress.street2"
                        data-qa="billing-street2"
                        label="Street address 2"
                        name="billing-street2"
                    />

                    <ds-input-field
                        v-if="displayField('billingCity', 'billingAddress')"
                        v-model="contactRecordDetails.billingAddress.city"
                        data-qa="billing-city"
                        label="City"
                        name="billing-city"
                    />

                    <ds-input-field
                        v-if="displayField('billingState', 'billingAddress')"
                        v-model="contactRecordDetails.billingAddress.state"
                        data-qa="billing-state"
                        label="State"
                        name="billing-state"
                    />

                    <ds-input-field
                        v-if="displayField('billingPostalCode', 'billingAddress')"
                        v-model="contactRecordDetails.billingAddress.postalCode"
                        data-qa="billing-postal-code"
                        label="Postal code"
                        name="billing-postalcode"
                    />

                    <ds-multiselect
                        v-if="displayField('billingCountry', 'billingAddress')"
                        v-model="contactRecordDetails.billingAddress.countryCode"
                        :options="countryDrop"
                        bind-value-only
                        data-qa="billing-country"
                        name="billing-country"
                        placeholder="Country"
                        searchable
                    />
                </div>
            </div>

            <div v-if="checkHeadersDisplay('shippingAddress')" class="profile-label">
                <div class="section-header">
                    <h3>Shipping Address</h3>

                    <ds-text-button
                        dense
                        data-qa="from-billing-shipping"
                        @click="fromBillingToShipping"
                    >
                        <span>From billing address</span>
                    </ds-text-button>
                </div>

                <div class="input-grid">
                    <ds-input-field
                        v-if="displayField('shippingStreetAddress1', 'shippingAddress')"
                        v-model="contactRecordDetails.shippingAddress.street1"
                        data-qa="shipping-street1"
                        label="Street address 1"
                        name="shipping-street1"
                    />

                    <ds-input-field
                        v-if="displayField('shippingStreetAddress2', 'shippingAddress')"
                        v-model="contactRecordDetails.shippingAddress.street2"
                        data-qa="shipping-street2"
                        label="App/Suite/Other"
                        name="shipping-street2"
                    />

                    <ds-input-field
                        v-if="displayField('shippingCity', 'shippingAddress')"
                        v-model="contactRecordDetails.shippingAddress.city"
                        data-qa="shipping-city"
                        label="City"
                        name="shipping-city"
                    />

                    <ds-input-field
                        v-if="displayField('shippingState', 'shippingAddress')"
                        v-model="contactRecordDetails.shippingAddress.state"
                        data-qa="shipping-state"
                        label="State"
                        name="shipping-state"
                    />

                    <ds-input-field
                        v-if="displayField('shippingPostalCode', 'shippingAddress')"
                        v-model="contactRecordDetails.shippingAddress.postalCode"
                        data-qa="shipping-postal-code"
                        label="Postal code"
                        name="shippingpostalcode"
                    />

                    <ds-multiselect
                        v-if="displayField('shippingCountry', 'shippingAddress')"
                        v-model="contactRecordDetails.shippingAddress.countryCode"
                        :options="countryDrop"
                        bind-value-only
                        data-qa="shipping-country"
                        name="shipping-country"
                        placeholder="Country"
                        searchable
                    />
                </div>
            </div>

            <div v-if="checkHeadersDisplay('optionalAddress')" class="profile-label">
                <div class="section-header">
                    <h3>Optional Address</h3>

                    <ds-text-button
                        dense
                        data-qa="from-billing-optional"
                        @click="fromBillingToOptional"
                    >
                        <span>From billing address</span>
                    </ds-text-button>
                </div>

                <div class="input-grid">
                    <ds-input-field
                        v-if="displayField('optionalStreetAddress1', 'optionalAddress')"
                        v-model="contactRecordDetails.optionalAddress.street1"
                        data-qa="optional-street1"
                        label="Street address 1"
                        name="optional-street1"
                    />

                    <ds-input-field
                        v-if="displayField('optionalStreetAddress2', 'optionalAddress')"
                        v-model="contactRecordDetails.optionalAddress.street2"
                        data-qa="optional-street2"
                        label="App/Suite/Other"
                        name="optional-street2"
                    />

                    <ds-input-field
                        v-if="displayField('optionalCity', 'optionalAddress')"
                        v-model="contactRecordDetails.optionalAddress.city"
                        data-qa="optional-city"
                        label="City"
                        name="optional-city"
                    />

                    <ds-input-field
                        v-if="displayField('optionalState', 'optionalAddress')"
                        v-model="contactRecordDetails.optionalAddress.state"
                        data-qa="optional-state"
                        label="State"
                        name="optional-state"
                    />

                    <ds-input-field
                        v-if="displayField('optionalPostalCode', 'optionalAddress')"
                        v-model="contactRecordDetails.optionalAddress.postalCode"
                        data-qa="optional-postal-code"
                        label="Postal code"
                    />

                    <ds-multiselect
                        v-if="displayField('optionalCountry', 'optionalAddress')"
                        v-model="contactRecordDetails.optionalAddress.countryCode"
                        :options="countryDrop"
                        bind-value-only
                        data-qa="optional-country"
                        name="optional-country"
                        placeholder="Country"
                        searchable
                    />
                </div>
            </div>

            <div v-if="checkHeadersDisplay('languageAndTimeZone')" class="profile-label">
                <div class="section-header">
                    <h3>Language &amp; Time Zone</h3>
                </div>

                <div class="input-grid">
                    <ds-multiselect
                        v-if="displayField('language', 'languageAndTimeZone')"
                        v-model="contactRecordDetails.language"
                        :options="languageDrop"
                        bind-value-only
                        data-qa="language"
                        name="language"
                        placeholder="Language"
                        searchable
                    />

                    <ds-multiselect
                        v-if="displayField('timezone', 'languageAndTimeZone')"
                        v-model="contactRecordDetails.timeZone"
                        :options="timeZonesDrop"
                        bind-value-only
                        data-qa="time-zone"
                        name="timezone"
                        placeholder="Time zone"
                        searchable
                    />
                </div>
            </div>

            <div v-if="checkHeadersDisplay('otherFields')" class="profile-label">
                <div class="section-header">
                    <h3>Other Fields</h3>
                </div>

                <div class="input-grid">
                    <ds-input-field
                        v-if="displayField('assistantName', 'otherFields')"
                        v-model="contactRecordDetails.assistantName"
                        data-qa="assistant-name"
                        label="Assistant name"
                        name="assistantname"
                    />

                    <ds-input-field
                        v-if="displayField('assistantPhone', 'otherFields')"
                        v-model="contactRecordDetails.assistantPhone"
                        data-qa="assistant-phone"
                        label="Assistant phone"
                        name="assistantphone"
                    />

                    <ds-input-field
                        v-if="displayField('SSN', 'otherFields')"
                        v-model="contactRecordDetails.ssn"
                        data-qa="ssn"
                        label="SSN"
                        name="ssn"
                    />

                    <ds-date-picker
                        v-if="displayField('birthday', 'otherFields')"
                        v-model="contactRecordDetails.birthday"
                        data-qa="birthday"
                        label="Birthday"
                        name="birthday"
                        date-only
                    />

                    <ds-input-field
                        v-if="displayField('spouseName', 'otherFields')"
                        v-model="contactRecordDetails.spouseName"
                        label="Spouse name"
                        name="spousename"
                        data-qa="spousename"
                    />

                    <ds-date-picker
                        v-if="displayField('anniversary', 'otherFields')"
                        v-model="contactRecordDetails.anniversary"
                        data-qa="anniversary"
                        label="Anniversary"
                        name="anniversary"
                        date-only
                    />

                    <ds-select-field
                        v-if="displayField('title', 'otherFields')"
                        v-model="contactRecordDetails.title"
                        :options="titlesDrop"
                        bind-value-only
                        data-qa="title"
                        label="Title"
                        name="title"
                    />

                    <ds-select-field
                        v-if="displayField('suffix', 'otherFields')"
                        v-model="contactRecordDetails.suffix"
                        :options="suffixsDrop"
                        bind-value-only
                        data-qa="suffix"
                        label="Suffix"
                        name="suffix"
                    />

                    <ds-input-field
                        v-if="displayField('middleName', 'otherFields')"
                        v-model="contactRecordDetails.middleName"
                        data-qa="middle-name"
                        label="Middle name"
                        name="middlename"
                    />

                    <ds-input-field
                        v-if="displayField('nickName', 'otherFields')"
                        v-model="contactRecordDetails.nickname"
                        data-qa="nick-name"
                        label="NickName"
                        name="nickname"
                    />

                    <ds-input-field
                        v-if="displayField('referralCode', 'otherFields')"
                        v-model="contactRecordDetails.referralCode"
                        data-qa="referral-code"
                        label="Referral code"
                        name="referralcode"
                    />

                    <ds-input-field
                        v-if="isUsernamePasswordEnabled && displayField('userName', 'otherFields')"
                        v-model="contactRecordDetails.userName"
                        data-qa="user-name"
                        label="User name"
                        name="username"
                    />

                    <ds-input-field
                        v-if="isUsernamePasswordEnabled && displayField('password', 'otherFields')"
                        v-model="contactRecordDetails.password"
                        data-qa="password"
                        label="Password"
                        type="password"
                    />
                </div>
            </div>
        </div>

        <Company ref="companyModal" />
    </div>
</template>

<script>
import axios from 'axios';
import ContactInfoHeader from './ContactInfoHeader';
import Company from './Company';
import cloneDeep from 'lodash.clonedeep';
import moment from 'moment';

export default {
    components: {
        ContactInfoHeader,
        Company,
    },

    props: {
        id: [String, Number],
        backUrl: String,
        userId: [String, Number],
        layoutId: [String, Number],
        appId: [String, Number],
    },

    data() {
        return {
            url: '/app/contact',
            searchCriteria: '',
            options: {
                phoneTypes: [],
                languages: [],
                country: [],
                personType: [],
                leadSources: [],
            },
            checkEmailLockdown: false,
            isBardBuilderEnabled: false,
            isEmailStatusFullNameEnabled: false,
            contactRecordDetails: {
                contactId: '',
                firstName: '',
                lastName: '',
                companyID: '',
                templateId: '',
                mailId: '',
                jobTitle: '',
                personType: '',
                leadSource: '',
                owner: '',
                notes: '',
                email: '',
                email2: '',
                email3: '',
                website: '',
                twitter: '',
                facebook: '',
                linkedin: '',
                instragram: '',
                snapchat: '',
                youtube: '',
                pinterest: '',
                phone1: {
                    number: '',
                    extension: '',
                    type: '',
                },
                phone2: {
                    number: '',
                    extension: '',
                    type: '',
                },
                phone3: {
                    number: '',
                    extension: '',
                    type: '',
                },
                phone4: {
                    number: '',
                    extension: '',
                    type: '',
                },
                phone5: {
                    number: '',
                    extension: '',
                    type: '',
                },

                fax1: '',
                fax2: '',
                billingAddress: {
                    street1: '',
                    street2: '',
                    city: '',
                    state: '',
                    postalCode: '',
                },
                shippingAddress: {
                    street1: '',
                    street2: '',
                    city: '',
                    state: '',
                    postalCode: '',
                },
                optionalAddress: {
                    street1: '',
                    street2: '',
                    city: '',
                    state: '',
                    postalCode: '',
                },
                language: '',
                timeZone: '',
                assistantName: '',
                assistantPhone: '',
                ssn: '',
                birthday: '',
                spouseName: '',
                anniversary: '',
                title: '',
                suffix: '',
                middleName: '',
                nickname: '',
                referralCode: '',
                userName: '',
                password: '',
            },
            contact: {},
            date: '',
            notes: '',
            maxCharacter: 70000,
            remainCharacterText: '',
            leadScoreValue: '',
            companies: [],
            value: '',
            isContactInfoChanged: false,
            contactDetails: {},
            contactPermissionDTO: {},
            selectedLayoutId: 0,
            headersList: {},
            headerDisplay: {},
            limit: 0,
            maxLimit: 1000,
            offset: 0,
            isContactCompaniesLimitEnabled: false,
            featureFlagValues: [],
            isLinkToCompanyRecordEnabled: false,
            isUsernamePasswordEnabled: false,
        };
    },
    created() {
        this.selectedLayoutId = Number(this.layoutId);
        this.load();
        this.companies.push({ label: 'None', value: '' });

        this.$root.$on('DROPDOWN_LOADED', (dropDowns) => {
            this.options = dropDowns;
        });

        this.$root.$on('CONTACTPERMISSIONS', (contactPermissions) => {
            this.contactPermissionDTO = contactPermissions;
        });

        this.$root.$on('COMPANY_DETAILS', (newCompany) => {
            if (newCompany !== null) {
                this.companies.push({
                    label: newCompany.companyName,
                    value: Number(newCompany.id),
                });
                this.contactRecordDetails.companyID = Number(newCompany.id);
            }
        });
        this.arrangeHeaderData();
    },

    beforeDestroy() {
        this.$root.$off('DROPDOWN_LOADED');
        this.$root.$off('COMPANY_DETAILS');
        this.$root.$off('UPDATE_CONTACT_STATUS');
    },

    mounted() {
        this.$root.$on('UPDATE_CONTACT_STATUS', (isContactUpdate) => {
            if (isContactUpdate) {
                this.isContactInfoChanged = false;
                this.contactDetails = cloneDeep(this.contactRecordDetails);
                this.$root.$emit('IS_CONTACT_INFO_CHANGED', this.isContactInfoChanged);
            }
        });

        this.$root.$on('LAYOUTID_TAB', (data) => {
            this.selectedLayoutId = Number(data.id);
        });
    },

    watch: {
        contactRecordDetails: {
            deep: true,
            handler() {
                this.isContactInfoChanged = JSON.stringify(this.contactRecordDetails) !== JSON.stringify(this.contactDetails);

                this.$root.$emit('IS_CONTACT_INFO_CHANGED', this.isContactInfoChanged);
            },
        },
    },

    computed: {
        checkHeadersDisplay() {
            return (headerName) => {
                return this.checkField(headerName) || this.fieldSearchHeaderDispaly(headerName) || this.headerDisplay[headerName];
            };
        },

        displayField() {
            // searching with field names should return true if exist
            return (field, head) => {
                return this.checkField(field) || this.headerDisplay[head];
            };
        },

        fieldSearchHeaderDispaly() {
            // searching with field name should return header names ex: General information
            return (param) => {
                const searchField = this.searchCriteria?.replace(/\s+/g, '');
                const headsToDisplay = Object.keys(this.headersList).filter((key) => {
                    return this.headersList[key].toString().replace(/\s+/g, '').toLowerCase().includes(searchField.toLowerCase());
                });

                return headsToDisplay.includes(param);
            };
        },

        checkField() {
            return (data) => {
                const searchField = this.searchCriteria?.replace(/\s+/g, '');

                // if searching with header name should make header value true, else false
                this.headerDisplay.generalInformation = 'generalInformation'.toLowerCase().includes(searchField.toLowerCase());
                this.headerDisplay.accountSummaryAndLeadScore = 'accountSummaryAndLeadScore'.toLowerCase().includes(searchField.toLowerCase());
                this.headerDisplay.personNotes = 'personNotes'.toLowerCase().includes(searchField.toLowerCase());
                this.headerDisplay.emailSocialMedia = 'emailSocialMedia'.toLowerCase().includes(searchField.toLowerCase());
                this.headerDisplay.phoneAndFax = 'phoneAndFax'.toLowerCase().includes(searchField.toLowerCase());
                this.headerDisplay.billingAddress = 'billingAddress'.toLowerCase().includes(searchField.toLowerCase());
                this.headerDisplay.shippingAddress = 'shippingAddress'.toLowerCase().includes(searchField.toLowerCase());
                this.headerDisplay.optionalAddress = 'optionalAddress'.toLowerCase().includes(searchField.toLowerCase());
                this.headerDisplay.languageAndTimeZone = 'languageAndTimeZone'.toLowerCase().includes(searchField.toLowerCase());
                this.headerDisplay.otherFields = 'otherFields'.toLowerCase().includes(searchField.toLowerCase());

                return data?.toLowerCase().includes(searchField.toLowerCase());
            };
        },

        personType() {
            return this.options.personType;
        },

        ownersDrop() {
            return this.options.owners;
        },

        countryDrop() {
            return this.options.country;
        },

        phoneTypeDrop() {
            return this.options.phoneTypes;
        },

        languageDrop() {
            return this.options.languages;
        },

        timeZonesDrop() {
            return this.options.timeZones;
        },

        suffixsDrop() {
            return this.options.suffixs;
        },

        titlesDrop() {
            return this.options.titles;
        },

        leadSourcesDrop() {
            if (!this.options.leadSources) {
                return [];
            }

            const cloneLeadSources = cloneDeep(this.options.leadSources);
            const leadSource = [{ label: 'None', value: 0 }];
            const leadSourceList = cloneLeadSources.sort((str1, str2) => {
                return str1.label.localeCompare(str2.label);
            });

            leadSource.push(...leadSourceList);

            return leadSource;
        },
    },

    methods: {
        clearSearch() {
            this.searchCriteria = '';
        },

        arrangeHeaderData() {
            // preparing map object, header with fields
            this.headersList = {};

            const address = ['street1', 'street2', 'city', 'state', 'postalCode', 'countryCode'];

            this.headersList.generalInformation = ['firstName', 'lastName', 'jobTitle', 'companies', 'personType', 'leadsource', 'owners'];
            this.headersList.accountSummaryAndLeadScore = ['amountPaid', 'balancedue', 'LeadScore', 'Invoices'];
            this.headersList.personNotes = ['PersonNotes'];
            this.headersList.emailSocialMedia = ['email', 'email2', 'email3', 'website', 'twitter', 'facebook', 'linkedin', 'instagram', 'snapchat', 'youtube', 'pinterest'];
            this.headersList.phoneAndFax = ['phone1', 'phone2', 'phone3', 'phone4', 'phone5', 'number', 'extension', 'type', 'fax1', 'fax2'];
            this.headersList.billingAddress = address;
            this.headersList.shippingAddress = address;
            this.headersList.optionalAddress = address;
            this.headersList.otherFields = ['assistantName', 'assistantPhone', 'ssn', 'birthday', 'spouseName', 'anniversary', 'title', 'suffix', 'middleName', 'nickname', 'referralCode', 'userName', 'password'];
            this.headersList.languageAndTimeZone = ['language', 'timeZone'];
        },

        dateFormatDate(date, format = 'll') {
            return moment(date).isValid() ? moment(date).format(format) : '';
        },

        viewCompany() {
            window.open(`/Company/manageCompany.jsp?view=edit&ID=${this.contactRecordDetails.companyID}`);
        },

        emailStatus(email) {
            return `/app/emailOptStatus/manageOptStatus?contactId=${this.id}&email=${email}`;
        },

        socialFieldEmptyCheck(socialField) {
            return socialField !== '';
        },

        async load() {
            await this.getCompanies();
            this.getContactRecordDetails();
            this.isEmailLockdown();
            this.getFeatureFlagValue();
            this.getEmailStatusFeatureFlagValue();
        },

        async getContactRecordDetails() {
            const url = `/app/contact/${this.id}`;

            try {
                const response = await axios.get(url, {
                    headers: {
                        Accept: 'application/json',
                    },
                });

                this.contactRecordDetails = JSON.parse(JSON.stringify(response.data).replace(/:null/gi, ':""'));
                this.leadScoreValue = this.contactRecordDetails.leadScore;

                if (this.leadScoreValue !== 0) {
                    if (this.leadScoreValue <= 3) {
                        this.leadScoreValue = 2;
                    } else if (this.leadScoreValue <= 6) {
                        this.leadScoreValue = 4;
                    } else {
                        this.leadScoreValue = 5;
                    }
                }
                const formattedDate = new Date();
                const format = 'MM-DD-YYYY';

                this.contactDetails = cloneDeep(this.contactRecordDetails);

                if (this.layoutId > 0) {
                    this.$root.$emit('CONTACT_RECORD_DETAILS', this.contactRecordDetails);
                }
                this.date = moment(formattedDate).format(format);
            } catch (error) {
                this.$error({ message: 'error while loading Contact Details' });
            }
        },

        viewInvoices() {
            window.open(`/Reports/searchTemplate.jsp?reportClass=AllSales&view=resultsPage&RunSearch=true&ContactId_DATA=${this.id}&Date_DATA=1-1-1970&Date_DATA2=${this.date}`);
        },

        async contactEmail() {
            try {
                const response = await axios.get('/app/contact/emailId');

                this.contact = response.data;

                window.open(`/Mail/compose.jsp?id=${this.contact.mailId}&templateid=${
                    this.contact.templateId}&Mail0ToAddress_text=${
                    this.contactRecordDetails.email}&mergeid=${this.id}`);
            } catch (error) {
                this.$error({ message: 'error while loading Contact Details' });
            }
        },

        webSite() {
            window.open(`https://${this.contactRecordDetails.website}`);
        },

        facebook() {
            window.open(`https://www.facebook.com/${this.contactRecordDetails.facebook}`);
        },

        twitter() {
            window.open(`https://twitter.com/${this.contactRecordDetails.twitter}`);
        },

        youtube() {
            window.open(`http://www.youtube.com/${this.contactRecordDetails.youtube}`);
        },

        pinterest() {
            window.open(`https://www.pinterest.com/${this.contactRecordDetails.pinterest}`);
        },

        snapchat() {
            window.open(`https://www.snapchat.com/${this.contactRecordDetails.snapchat}`);
        },

        instagram() {
            window.open(`https://www.instagram.com/${this.contactRecordDetails.instragram}`);
        },

        linkedIn() {
            window.open(`https://www.linkedin.com/${this.contactRecordDetails.linkedin}`);
        },

        fromBillingToShipping() {
            this.contactRecordDetails.shippingAddress = cloneDeep(
                this.contactRecordDetails.billingAddress,
            );
        },

        fromBillingToOptional() {
            this.contactRecordDetails.optionalAddress = cloneDeep(
                this.contactRecordDetails.billingAddress,
            );
        },

        addCompanyModal() {
            this.$refs.companyMultiselect.toggle();
            this.$refs.companyModal.showModal();
        },

        async getCompanies() {
            try {
                await this.getContactCompaniesLimitFlagValue();
                let url = '';

                if (this.isContactCompaniesLimitEnabled) {
                    url = `${this.url}/getCompanies?limit=${this.limit}&offset=${this.offset}`;
                } else {
                    url = `${this.url}/getCompanies?limit=${this.maxLimit}&offset=${this.offset}`;
                }
                const response = await axios.get(url);

                const companies = [{ label: 'None', value: Number(0) }];

                this.companies = Object.entries(response.data).map((entry) => {
                    companies.push({ label: entry[1], value: Number(entry[0]) });

                    return { label: entry[1], value: Number(entry[0]) };
                });
                this.companies = companies;
            } catch (error) {
                this.$error({ message: 'Unable to load Companies' });
            }
        },

        async getContactCompaniesLimitFlagValue() {
            try {
                const response = await axios.get(`${this.url}/isContactCompaniesLimitEnabled`);

                this.isContactCompaniesLimitEnabled = response.data;
            } catch (e) {
                this.$error({ message: 'Unable to get company limit feature flag value' });
            }
        },

        async launchBardEmailBroadcast(email) {
            const response = await axios.post(`/app/emailBroadcast/createListForContact?contactId=${this.id}&emailAddress=${email}`);

            if (window.sessionStorage) {
                sessionStorage.setItem('fromContact', true);
                sessionStorage.setItem('contactId', this.id);
                sessionStorage.setItem('backUrl', this.backUrl);

                if (this.isBardBuilderEnabled) {
                    window.location = `/app/email/broadcast/gallery?listId=${response.data}`;
                } else {
                    this.contactEmail();
                }
            }
        },

        async isEmailLockdown() {
            try {
                const response = await axios.get('/app/broadcast/checkEmailLockdown');

                this.checkEmailLockdown = response.data;
            } catch (e) {
                this.$error({ message: 'Unable get checkEmailLockdown' });
            }
        },

        async getFeatureFlagValue() {
            try {
                const response = await axios.get('/app/broadcast/getFeatureflags');

                this.featureFlagValues = response.data;
                this.isBardBuilderEnabled = this.featureFlagValues.isBardBuilderEnabled;
                this.isLinkToCompanyRecordEnabled = this.featureFlagValues.isLinkToCompanyRecordEnabled;
                this.isUsernamePasswordEnabled = this.featureFlagValues.isUsernamePasswordEnabled;
            } catch (e) {
                this.$error({ message: 'Unable get featureFlag value' });
            }
        },

        async getEmailStatusFeatureFlagValue() {
            try {
                const response = await axios.get('/app/contact/isEmailStatusFullNameEnabled');

                this.isEmailStatusFullNameEnabled = response.data;
            } catch (e) {
                this.$error({ message: 'Unable get email status featureFlag value' });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.profile-label {
    padding: $spacing-300 0 $spacing-100;
}

.external-look {
    --icon-color: blue;
    --icon-size: 1rem;
    margin-top: 3px;
    cursor: pointer;
}

.account {
    background-color: $color-white;
    color: $color-gray-80;
}

.layout {
    display: flex;
    flex-direction: column;
}

.input-grid {
    --input-margin-bottom: 0;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: $spacing-300;
}

.input-group:empty {
    display: none;
}

.search-input-field {
    --input-margin-bottom: 0;
}

.search {
    width: px-to-rem(320px);
    --icon-color: #{$color-gray-600};
    margin-top: $spacing-200;
    justify-content: flex-start;
}

.clear-icon {
    --icon-color: #{$color-ink};
    cursor: pointer;
}

.search-save-button {
    justify-content: space-between;
    display: flex;
    margin-top: $spacing-200;
}

.account-summary {
    display: flex;
    align-items: center;

    > :not(:last-child) {
        @include margin-end($spacing-200);
    }
}

.person-notes {
    --input-margin-bottom: 0;
}

.input-group {
    > *:first-child {
        flex: 2;
    }
}

.due {
    font-size: $font-size-md;
    color: $color-red;
    margin-bottom: 0;
}

.paid {
    font-size: $font-size-md;
    margin-bottom: 0;
}

.summary {
    font-size: 0.769rem;
    display: inline-block;
    color: $color-gray-90;
}

.lead-score {
    font-size: 0.769rem;
    display: inline-block;
    color: $color-black;
}

.email-status {
    font-size: 0.769rem;
    color: $color-gray-90;
    margin-bottom: $spacing-200;
}

.email2-status {
    font-size: 0.769rem;
    color: $color-gray-90;
    margin-top: $spacing-100;
}

#sites {
    margin-right: $spacing-400;
}

.status {
    font-size: 0.83rem;
    display: inline-flex;
    color: $color-blue;
    font-weight: bold;
    font-style: normal;
}

.score {
    font-style: normal;
    font-size: 0.83rem;
    padding: $spacing-100;
}

.slides {
    display: block;
    flex-flow: row wrap;
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $spacing-400;
    border-top: 1px solid $color-gray-30;
    padding-top: $spacing-300;
}

.email-allign {
    display: inline-flex;
}

.fire-icon {
    --icon-size: 1rem;
    --icon-color: #{$color-black};
}

@media screen and (max-width: 1200px) {
    .input-grid {
        grid-template-columns: 1fr 1fr;
    }
}

@media screen and ($small) {
    .input-grid {
        grid-template-columns: 1fr;
    }

    .slides::-webkit-scrollbar {
        display: none;
    }

    .layout {
        flex-direction: column;
        margin: auto;
    }

    .email-status {
        margin-top: 0.2rem;
    }

    #notes {
        margin-bottom: $spacing-800;
    }

    .account-summary {
        flex-direction: column;
        align-items: flex-start;

        > :not(:last-child) {
            margin: 0 0 $spacing-200;
        }
    }
}
</style>
